import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormElement, Field, Form, FormHandle } from '@progress/kendo-react-form'
import MessageAlert from 'components/Alerts/MessageAlert/MessageAlert'
import FormInput from 'components/Form/FormInput'
import FormSkeleton from 'components/Form/FormSkeleton'
import { getErrors, focusFirstError } from 'core/helpers'
import { V1ApiUsersClient } from 'core/http-common'
import toast from 'core/toast'
import { useState, useRef, useEffect } from 'react'
import { Row, Col, Button, Badge } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { startLoadingAction, endLoadingAction } from 'state/actions/appActions'
import { IRootState, AlertMessage } from 'types'
import SmsMultiFactorVerifyForm from './SmsMultiFactorVerifyForm'
import { setMultFactorVerified } from 'state/actions/userActions'
import Spinner from 'components/Spinner/Spinner'
import { AppDispatch } from 'state/store'
import { MultiFactorVerifiedDto, GoogleAuthenticatorSetupDto, UserMultiFactorSettingsJoinedDto, MultiFactorTypeEnum, ConfirmGoogleAuthenticatorSetupDto, EnableGoogleAuthenticatorSetupDto } from '@v1UsersApi'

interface IFormState {
    code: string
}

interface IProps {
    onSetup?: Function,
    onVerificationRequired?: Function,
    onVerified?: (dto: MultiFactorVerifiedDto) => void
}

const GoogleAuthenticatorSetupForm = ({ onSetup, onVerificationRequired, onVerified }: IProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const loading = useSelector((state: IRootState) => state.app.loading)
    const multiFactorGuid = useSelector((state: IRootState) => state.user.multiFactorGuid)
    const multiFactorGuidExpireDate = useSelector((state: IRootState) => state.user.multiFactorGuidExpireDate)

    const formRef = useRef<FormHandle>(null)
    const [alert, setAlert] = useState<AlertMessage>()
    const [googleSetupData, setGoogleSetupData] = useState<GoogleAuthenticatorSetupDto>()
    const [userGoogleSettings, setUserGoogleSettings] = useState<UserMultiFactorSettingsJoinedDto>()
    const [showSmsVerification, setShowSmsVerification] = useState(false)
    const [finalizingSetup, setFinalizingSetup] = useState(false)
    const fetch = async () => {
        try {
            await dispatch(startLoadingAction())
            setUserGoogleSettings((await V1ApiUsersClient.getUserMultiFactorSettings()).data?.find(f => f.multiFactorTypeId === MultiFactorTypeEnum.Google))
            setGoogleSetupData(await V1ApiUsersClient.startGoogleAuthenticatorSetup())
        }
        catch (e) {
            setAlert({ variant: 'danger', messages: getErrors(e) })
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    useEffect(() => {
        fetch()
    }, [])

    useEffect(() => {
        let input = document.getElementById('code')
        input?.focus()
    }, [googleSetupData])

    const confirmCode = async (form: IFormState) => {
        dispatch(startLoadingAction())
        setAlert({ variant: 'info', messages: [] })

        try {
            const response = await V1ApiUsersClient.confirmGoogleAuthenticatorSetup(
                new ConfirmGoogleAuthenticatorSetupDto({ code: form.code })
            )

            if (!response.success) {
                setAlert({ variant: 'danger', messages: ['Invalid code'] })
                return
            }

            if (multiFactorGuid && multiFactorGuid.trim() && multiFactorGuidExpireDate > new Date()) {
                setFinalizingSetup(true)
                await completeSetup(multiFactorGuid)
                setShowSmsVerification(false)
                return
            }

            if (onVerificationRequired) {
                onVerificationRequired()
            }

            setShowSmsVerification(true)
        } catch (e) {
            setAlert({ variant: 'danger', messages: getErrors(e) })
        } finally {
            await dispatch(endLoadingAction())
        }
    }

    const completeSetup = async (multiFactorGuid: string) => {
        try {
            dispatch(startLoadingAction())
            setAlert({ variant: 'info', messages: [] })
            let response = await V1ApiUsersClient.enableGoogleAuthenticatorSetup(
                new EnableGoogleAuthenticatorSetupDto({ multiFactorGuid }))

            if (response.success) {
                toast.success('Google Authenticator setup successfully')
                if (onSetup)
                    onSetup()
            }
        }
        catch (e) {
            setAlert({ variant: 'danger', messages: getErrors(e) })
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    if (finalizingSetup) {
        return <Spinner center={true} />
    }

    if (showSmsVerification) {
        return <SmsMultiFactorVerifyForm
            onVerified={async (e: MultiFactorVerifiedDto) => {
                setFinalizingSetup(true)
                dispatch(setMultFactorVerified(e))
                await completeSetup(e.multiFactorGuid)
                if (onVerified)
                    onVerified(e)
                setShowSmsVerification(false)
            }}
        />
    }

    return (
        <div>
            <h5 className='text-center'>
                {!userGoogleSettings
                    ? <FormSkeleton style={{ height: '200px' }} />
                    : <span>
                        <img src={userGoogleSettings?.logoUrl} style={{ height: '50px' }} />
                        Google Authenticator {userGoogleSettings?.recommended && <Badge bg={'success'}>Recommended</Badge>}
                    </span>
                }
            </h5>

            {!userGoogleSettings
                ? <FormSkeleton style={{ height: '50px' }} />
                : <div>
                    <Form
                        ref={formRef}
                        onSubmit={(e) => { confirmCode(e as IFormState) }}
                        render={(formRenderProps) => (
                            <FormElement>
                                <Row>
                                    <Col xs={12} className='text-center mt-3'>
                                        <b>Step 1:</b> Download the Google Authenticator app from {' '}
                                        <a href='https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_US&gl=US' target='_blank'>Google Play</a> or the
                                        {' '}<a href='https://apps.apple.com/us/app/google-authenticator/id388497605' target='_blank'>App Store</a> from your mobile device.

                                        {!googleSetupData
                                            ? <FormSkeleton style={{ height: '200px' }} />
                                            : <div className='mt-4'>
                                                <p>
                                                    <b>Step 2:</b> Scan this QR code with the Google Authenticator app.
                                                </p>
                                                {!googleSetupData
                                                    ? <FormSkeleton style={{ height: '200px' }} />
                                                    : <img
                                                        src={googleSetupData.qrCodeSetupImageUrl}
                                                        className='img-thumbnail mb-3'
                                                    />
                                                }
                                                <p>
                                                    <b>Step 3:</b> Find the 6 digit code for 'DCS' and enter it in the code field below.
                                                </p>
                                            </div>
                                        }
                                    </Col>
                                    <Col lg={{ span: 6, offset: 3 }}>
                                        {!googleSetupData
                                            ? <FormSkeleton />
                                            : <Field
                                                name='code'
                                                id='code'
                                                component={FormInput}
                                                label='Code:'
                                                autoComplete='false'
                                                autofocus
                                            />
                                        }
                                    </Col>
                                    <Col lg={{ span: 6, offset: 3 }}>
                                        {!googleSetupData
                                            ? <FormSkeleton style={{ height: '50px' }} />
                                            : <Button
                                                variant='primary'
                                                className='mt-2 btn-block'
                                                type='submit'
                                                disabled={!formRenderProps.allowSubmit || loading}
                                                onClick={e => focusFirstError(formRenderProps)}>
                                                <FontAwesomeIcon icon={['fas', 'arrow-circle-right']} /> Verify Code
                                            </Button>
                                        }
                                    </Col>
                                </Row>
                            </FormElement>
                        )}
                    />
                    <MessageAlert alert={alert} />
                </div>
            }
        </div>
    )
}
export default GoogleAuthenticatorSetupForm